import React, { useState, useEffect } from 'react';
import { MessageCircle, Heart, Reply, MoreVertical, Send, User } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { CommunityService } from '../../services/communityService';
import type { CommunityForum, ThreadReply } from '../../types/community';

interface ForumDetailsPanelProps {
  forum: CommunityForum | null;
  onClose: () => void;
}

const ForumDetailsPanel: React.FC<ForumDetailsPanelProps> = ({ forum, onClose }) => {
  const { language, isRTL, strings } = useLocalization();
  const { user } = useAuth();
  const { getForumCategoryById } = useSystemPreset();
  const [replies, setReplies] = useState<ThreadReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);
  const [likingReply, setLikingReply] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [childReplyContent, setChildReplyContent] = useState('');
  const [submittingChildReply, setSubmittingChildReply] = useState(false);

  // Get category details
  const category = forum ? getForumCategoryById(forum.categoryId) : null;

  useEffect(() => {
    if (forum) {
      loadReplies();
    }
  }, [forum]);

  const loadReplies = async () => {
    if (!forum) return;
    
    try {
      setLoading(true);
      console.log('🔄 Loading replies for forum:', forum.id);
      const repliesData = await CommunityService.getForumReplies(forum.id);
      console.log('📝 Loaded replies:', repliesData);
      console.log('📝 First reply author data:', repliesData[0] ? {
        authorName: repliesData[0].authorName,
        authorId: repliesData[0].authorId
      } : 'No replies');
      
      // Update replies with proper names if they have email as authorName
      const updatedReplies = await Promise.all(
        repliesData.map(async (reply) => {
          // If authorName looks like an email, try to fetch the proper name
          if (reply.authorName && reply.authorName.includes('@')) {
            try {
              const userDoc = await CommunityService.getUserById(reply.authorId);
              if (userDoc) {
                const userName = userDoc.name || 
                                userDoc.displayName || 
                                userDoc.fullName || 
                                userDoc.firstName + ' ' + userDoc.lastName || 
                                userDoc.email?.split('@')[0] || 
                                reply.authorName;
                
                return {
                  ...reply,
                  authorName: userName,
                  authorPhotoURL: userDoc.photoURL || userDoc.avatar || reply.authorPhotoURL,
                  authorTitle: userDoc.title || userDoc.jobTitle || userDoc.position || reply.authorTitle
                };
              }
            } catch (error) {
              console.warn('Could not fetch user data for reply:', reply.id, error);
            }
          }
          return reply;
        })
      );
      
      setReplies(updatedReplies);
    } catch (error) {
      console.error('❌ Error loading replies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !user?.id || !forum) return;

    try {
      setSubmittingReply(true);
      console.log('🔄 Submitting reply:', {
        forumId: forum.id,
        content: replyContent,
        userId: user.id,
        userName: user.name || user.email,
        userPhotoURL: user.photoURL
      });
      
      await CommunityService.addForumReply(forum.id, replyContent, user.id);
      setReplyContent('');
      setReplySuccess(true);
      await loadReplies(); // Reload replies
      console.log('✅ Reply submitted successfully');
      
      // Hide success message after 3 seconds
      setTimeout(() => setReplySuccess(false), 3000);
    } catch (error) {
      console.error('❌ Error submitting reply:', error);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleLikeReply = async (replyId: string) => {
    console.log('🔄 Like button clicked for reply:', replyId);
    console.log('🔄 Current user:', user?.id);
    console.log('🔄 Current forum:', forum?.id);
    
    if (!user?.id || !forum) {
      console.log('❌ Missing user or forum data');
      return;
    }

    try {
      setLikingReply(replyId);
      const reply = replies.find(r => r.id === replyId);
      if (!reply) {
        console.log('❌ Reply not found in local state');
        return;
      }

      const isLiked = reply.likedBy?.includes(user.id) || false;
      console.log('🔄 Current like status:', isLiked);
      console.log('🔄 Reply likedBy array:', reply.likedBy);
      
      if (isLiked) {
        console.log('🔄 Unliking reply...');
        await CommunityService.unlikeReply(forum.id, replyId, user.id);
      } else {
        console.log('🔄 Liking reply...');
        await CommunityService.likeReply(forum.id, replyId, user.id);
      }

      // Reload replies to get updated like counts
      console.log('🔄 Reloading replies...');
      await loadReplies();
    } catch (error) {
      console.error('❌ Error toggling like:', error);
    } finally {
      setLikingReply(null);
    }
  };

  const handleReplyToReply = (replyId: string) => {
    setReplyingTo(replyId);
    setChildReplyContent('');
  };

  const handleCancelChildReply = () => {
    setReplyingTo(null);
    setChildReplyContent('');
  };

  const handleSubmitChildReply = async (e: React.FormEvent, parentReplyId: string) => {
    e.preventDefault();
    if (!childReplyContent.trim() || !user?.id || !forum) return;

    try {
      setSubmittingChildReply(true);
      console.log('🔄 Submitting child reply:', {
        forumId: forum.id,
        parentReplyId,
        content: childReplyContent,
        userId: user.id
      });
      
      await CommunityService.addForumReply(forum.id, childReplyContent, user.id, parentReplyId);
      setChildReplyContent('');
      setReplyingTo(null);
      await loadReplies(); // Reload replies
      console.log('✅ Child reply submitted successfully');
    } catch (error) {
      console.error('❌ Error submitting child reply:', error);
    } finally {
      setSubmittingChildReply(false);
    }
  };

  const formatDate = (timestamp: string | number) => {
    const timestampNumber = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
    return new Date(timestampNumber).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!forum) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
            {language === 'ar' ? 'اختر منتدى لعرض التفاصيل' : 'Select a forum to view details'}
          </h3>
          <p className="text-slate-500 dark:text-slate-500">
            {language === 'ar' ? 'انقر على منتدى من القائمة لعرض المناقشات والردود' : 'Click on a forum from the list to view discussions and replies'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
      {/* Forum Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-700 rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
            <MessageCircle className="w-8 h-8" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">
              {forum.title}
            </h2>
            <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400 mb-2">
              <span className="flex items-center space-x-1">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  category?.color === 'blue' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  category?.color === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  category?.color === 'purple' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {category ? (language === 'ar' ? category.name_ar : category.name_en) : 'Unknown Category'}
                </span>
              </span>
              <span className="flex items-center space-x-1">
                <span>{replies.length} {language === 'ar' ? 'رد' : 'replies'}</span>
              </span>
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
              {forum.description}
            </p>
            <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-white mb-2">
                {language === 'ar' ? 'الموضوع' : 'Subject'}
              </h4>
              <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                {forum.subject}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Replies Section */}
      <div className="p-6 space-y-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-slate-500 dark:text-slate-400">
              {language === 'ar' ? 'جاري تحميل الردود...' : 'Loading replies...'}
            </p>
          </div>
        ) : replies.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
              {language === 'ar' ? 'لا توجد ردود بعد' : 'No replies yet'}
            </h3>
            <p className="text-slate-500 dark:text-slate-500 text-sm">
              {language === 'ar' ? 'كن أول من يشارك في هذا المناقشة' : 'Be the first to join this discussion'}
            </p>
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
              {language === 'ar' ? 'الردود' : 'Replies'} ({replies.length})
            </h3>
            <div className="space-y-4">
            {replies
              .filter(reply => !reply.parentReplyId) // Only show top-level replies
              .map((reply) => {
                const childReplies = replies.filter(child => child.parentReplyId === reply.id);
                return (
              <div key={reply.id} className="bg-slate-50 dark:bg-slate-700 rounded-xl p-5 border border-slate-200 dark:border-slate-600">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-blue-700 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden">
                    {reply.authorPhotoURL ? (
                      <img 
                        src={reply.authorPhotoURL} 
                        alt={reply.authorName}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-800 dark:text-white text-sm">
                        {reply.authorName || 'Anonymous'}
                      </span>
                      {reply.authorTitle && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded-full">
                          {reply.authorTitle}
                        </span>
                      )}
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {formatDate(reply.createdAt)}
                      </span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300 text-sm mb-3 whitespace-pre-wrap">
                      {reply.content}
                    </p>
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔄 Button clicked!', reply.id);
                          handleLikeReply(reply.id);
                        }}
                        disabled={likingReply === reply.id}
                        className={`flex items-center gap-1 transition-colors ${
                          reply.likedBy?.includes(user?.id || '') 
                            ? 'text-red-500 hover:text-red-600' 
                            : 'text-slate-500 hover:text-red-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {likingReply === reply.id ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Heart className={`w-4 h-4 ${reply.likedBy?.includes(user?.id || '') ? 'fill-current' : ''}`} />
                        )}
                        <span className="text-xs">{reply.likeCount}</span>
                      </button>
                      <button 
                        onClick={() => handleReplyToReply(reply.id)}
                        className="flex items-center gap-1 text-slate-500 hover:text-primary transition-colors"
                      >
                        <Reply className="w-4 h-4" />
                        <span className="text-xs">{language === 'ar' ? 'رد' : 'Reply'}</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Child Reply Form */}
                {replyingTo === reply.id && (
                  <div className="mt-4 ml-6 border-l-2 border-slate-200 dark:border-slate-600 pl-4">
                    <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                          {language === 'ar' ? 'رد على' : 'Reply to'} {reply.authorName}
                        </h4>
                        <button
                          onClick={handleCancelChildReply}
                          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                          ✕
                        </button>
                      </div>
                      <form onSubmit={(e) => handleSubmitChildReply(e, reply.id)} className="space-y-3">
                        <textarea
                          value={childReplyContent}
                          onChange={(e) => setChildReplyContent(e.target.value)}
                          placeholder={language === 'ar' ? 'اكتب ردك هنا...' : 'Write your reply here...'}
                          className="w-full p-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                          rows={3}
                          required
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleCancelChildReply}
                            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                          >
                            {language === 'ar' ? 'إلغاء' : 'Cancel'}
                          </button>
                          <button
                            type="submit"
                            disabled={!childReplyContent.trim() || submittingChildReply}
                            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {submittingChildReply ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                            {submittingChildReply ? 
                              (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : 
                              (language === 'ar' ? 'إرسال الرد' : 'Send Reply')
                            }
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                
                {/* Child Replies */}
                {childReplies.length > 0 && (
                  <div className="mt-4 ml-6 space-y-3">
                    {childReplies.map((childReply) => (
                      <div key={childReply.id} className="bg-slate-100 dark:bg-slate-600 rounded-lg p-4 border-l-2 border-slate-300 dark:border-slate-500">
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0 overflow-hidden">
                            {childReply.authorPhotoURL ? (
                              <img 
                                src={childReply.authorPhotoURL} 
                                alt={childReply.authorName}
                                className="w-8 h-8 rounded-lg object-cover"
                              />
                            ) : (
                              <User className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-slate-800 dark:text-white text-sm">
                                {childReply.authorName || 'Anonymous'}
                              </span>
                              {childReply.authorTitle && (
                                <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-500 px-2 py-1 rounded-full">
                                  {childReply.authorTitle}
                                </span>
                              )}
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {formatDate(childReply.createdAt)}
                              </span>
                            </div>
                            <p className="text-slate-700 dark:text-slate-300 text-sm mb-3 whitespace-pre-wrap">
                              {childReply.content}
                            </p>
                            <div className="flex items-center gap-4">
                              <button 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('🔄 Child reply like button clicked!', childReply.id);
                                  handleLikeReply(childReply.id);
                                }}
                                disabled={likingReply === childReply.id}
                                className={`flex items-center gap-1 transition-colors ${
                                  childReply.likedBy?.includes(user?.id || '') 
                                    ? 'text-red-500 hover:text-red-600' 
                                    : 'text-slate-500 hover:text-red-500'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {likingReply === childReply.id ? (
                                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <Heart className={`w-4 h-4 ${childReply.likedBy?.includes(user?.id || '') ? 'fill-current' : ''}`} />
                                )}
                                <span className="text-xs">{childReply.likeCount}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
            })}
            </div>
          </div>
        )}
      </div>

      {/* Reply Form */}
      {user && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                {language === 'ar' ? 'أضف رد' : 'Add Reply'}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {language === 'ar' ? 'شارك رأيك في هذا المناقشة' : 'Share your thoughts on this discussion'}
              </p>
            </div>
          </div>
            <form onSubmit={handleSubmitReply} className="space-y-4">
              {replySuccess && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-green-800 dark:text-green-200 text-sm">
                    {language === 'ar' ? 'تم إرسال الرد بنجاح!' : 'Reply sent successfully!'}
                  </p>
                </div>
              )}
              <div>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder={language === 'ar' ? 'اكتب ردك هنا...' : 'Write your reply here...'}
                  className="w-full p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={4}
                  required
                />
              </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!replyContent.trim() || submittingReply}
                className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl font-medium transition-colors duration-300 flex items-center space-x-2 rtl:space-x-reverse shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReply ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
                <span>
                  {submittingReply ? 
                    (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : 
                    (language === 'ar' ? 'إرسال الرد' : 'Send Reply')
                  }
                </span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ForumDetailsPanel;
